import React, { createContext, useContext, useEffect, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { StickyNote } from 'lucide-react';
import apiClient from '../../utils/apiClient';
import './SongReminders.css';

interface Reminder { song_id: number; note: string; completed: boolean; revision: number }
interface Draft extends Reminder { title: string }
interface ReminderContext {
  reminders: Reminder[];
  available: boolean;
  open: (songId: number, title: string) => void;
}
const Context = createContext<ReminderContext | null>(null);
const message = (error: any) => typeof error?.response?.data?.error === 'string'
  ? error.response.data.error : 'Could not load or save reminders. Please try again.';

export function SongRemindersProvider({ children }: { children: React.ReactNode }) {
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const reload = async () => {
    setLoading(true); setLoadError('');
    try { setReminders((await apiClient.get<Reminder[]>('/song-reminders')).data); }
    catch (error) { setLoadError(message(error)); }
    finally { setLoading(false); }
  };
  useEffect(() => { void reload(); }, []);
  const open = (songId: number, title: string) => {
    const saved = reminders.find(r => r.song_id === songId);
    setDraft({ song_id: songId, title, note: saved && !saved.completed ? saved.note : '',
      revision: saved?.revision || 0, completed: saved?.completed ?? true });
    setFormError('');
  };
  const save = async (completed: boolean) => {
    if (!draft || saving) return;
    setSaving(true); setFormError('');
    try {
      const saved = (await apiClient.put<Reminder>(`/song-reminders/${draft.song_id}`, {
        note: draft.note.trim(), revision: draft.revision, completed,
      })).data;
      setReminders(previous => [...previous.filter(r => r.song_id !== saved.song_id), saved]);
      setDraft(null);
    } catch (error) { setFormError(message(error)); }
    finally { setSaving(false); }
  };
  return <Context.Provider value={{ reminders, available: !loading && !loadError, open }}>
    {loadError && <Alert variant="warning" className="song-reminder-status" role="alert">
      Song reminders are unavailable. <Button variant="link" onClick={() => void reload()}>Retry reminders</Button>
    </Alert>}
    {children}
    <Modal show={!!draft} onHide={() => { if (!saving) setDraft(null); }} centered aria-labelledby="song-reminder-title"
      backdrop={saving ? 'static' : true} keyboard={!saving}>
      <Modal.Header closeButton={!saving} closeLabel="Close reminder"><Modal.Title id="song-reminder-title">For next time</Modal.Title></Modal.Header>
      {draft && <Form onSubmit={e => { e.preventDefault(); void save(false); }}>
        <Modal.Body>
          <p><strong>{draft.title}</strong></p>
          {formError && <Alert variant="danger" role="alert">{formError}
            <div><Button variant="link" disabled={loading} onClick={() => void reload()}>Reload reminders</Button></div>
          </Alert>}
          <Form.Group controlId="song-reminder-note">
            <Form.Label>Reminder</Form.Label>
            <Form.Control as="textarea" rows={4} autoFocus required maxLength={5000}
              disabled={saving} value={draft.note}
              placeholder="What would you like to revisit next time?"
              onChange={e => setDraft({ ...draft, note: e.target.value })} />
            <Form.Text>Only you can see this. It stays with the song until you mark it done.</Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          {!draft.completed && <Button variant="outline-success" disabled={saving || !draft.note.trim()}
            onClick={() => void save(true)}>Mark done</Button>}
          <Button variant="secondary" disabled={saving} onClick={() => setDraft(null)}>Close</Button>
          <Button type="submit" disabled={saving || !draft.note.trim()}>{saving ? 'Saving…' : 'Save reminder'}</Button>
        </Modal.Footer>
      </Form>}
    </Modal>
  </Context.Provider>;
}

export function SongReminderBadge({ songId, title }: { songId?: number; title: string }) {
  const context = useContext(Context);
  if (!context || !songId) return null;
  const reminder = context.reminders.find(r => r.song_id === songId && !r.completed);
  const label = `${reminder ? 'Reminder for' : 'Add reminder for'} ${title}`;
  return <button type="button" className={`song-reminder-badge${reminder ? ' has-reminder' : ''}`}
    disabled={!context.available} aria-label={label} title={reminder?.note || label}
    onClick={e => { e.preventDefault(); e.stopPropagation(); context.open(songId, title); }}>
    <StickyNote size={13} aria-hidden="true" />{reminder && <span>Reminder</span>}
  </button>;
}
